import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import CircularProgress from '@material-ui/core/CircularProgress'

import { getMemberDetails, saveMemberDetails } from '../../store/actions/memberDetailsActions'
import { formatPhone334, phoneDigits, PHONE_MAX_LENGTH } from './phoneFormat'

/**
 * "Confirm your details": the fields the membership contract is prefilled
 * from, shown before signing in both renewal flows. Everything but place of
 * birth is saved to the member record first (so the account page and the
 * contract match); place of birth is contract-only and handed to the caller.
 *
 * onConfirmed({ details, placeOfBirth }) runs after a successful save.
 */

const REQUIRED_MESSAGE = 'Required'
const FIELD_MESSAGES = {
    required: REQUIRED_MESSAGE,
    invalid: 'Please check this',
    too_long: 'Too long'
}


const EMPTY = {
    firstName: '', lastName: '', dateOfBirth: '', placeOfBirth: '', phoneNumber: '',
    address: '', mailingAddress: '', emergencyContactName: '', emergencyContact: ''
}

const labelProps = { shrink: true, style: { padding: 0 } }

const ConfirmDetails = ({ onConfirmed, continueLabel = 'Save and continue', intro }) => {
    const dispatch = useDispatch()
    const [form, setForm] = useState(EMPTY)
    const [sameMailing, setSameMailing] = useState(true)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(null)
    const [saving, setSaving] = useState(false)
    const [errors, setErrors] = useState({})
    const [saveError, setSaveError] = useState(null)

    const load = () => {
        setLoading(true)
        setLoadError(null)
        dispatch(getMemberDetails((err, data) => {
            setLoading(false)
            if (err || !data) {
                setLoadError((err && err.message) || "We couldn't load your details.")
                return
            }
            setForm({
                ...EMPTY,
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                dateOfBirth: data.dateOfBirth || '',
                phoneNumber: formatPhone334(data.phoneNumber),
                address: data.address || '',
                mailingAddress: data.mailingAddress || '',
                emergencyContactName: data.emergencyContactName || '',
                emergencyContact: formatPhone334(data.emergencyContact)
            })
            setSameMailing(!data.mailingAddress || data.mailingAddress === data.address)
        }))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [])

    const PHONE_FIELDS = ['phoneNumber', 'emergencyContact']
    const set = (key) => (e) => {
        // Phone fields format as you type: 3-3-4, digits only, at most 10.
        const value = PHONE_FIELDS.includes(key) ? formatPhone334(e.target.value) : e.target.value
        setForm((f) => ({ ...f, [key]: value }))
        setErrors((errs) => ({ ...errs, [key]: null }))
    }

    const check = () => {
        const errs = {}
        ;['firstName', 'lastName', 'dateOfBirth', 'placeOfBirth', 'phoneNumber', 'address'].forEach((k) => {
            if (!String(form[k] || '').trim()) errs[k] = REQUIRED_MESSAGE
        })
        if (form.phoneNumber && phoneDigits(form.phoneNumber).length !== 10) {
            errs.phoneNumber = 'Enter a 10-digit phone number'
        }
        if (form.emergencyContact && phoneDigits(form.emergencyContact).length !== 10) {
            errs.emergencyContact = 'Enter a 10-digit phone number'
        }
        return errs
    }

    const submit = () => {
        const errs = check()
        setErrors(errs)
        setSaveError(null)
        if (Object.keys(errs).length) return

        const details = {
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            dateOfBirth: form.dateOfBirth,
            phoneNumber: phoneDigits(form.phoneNumber),
            address: form.address.trim(),
            mailingAddress: sameMailing ? form.address.trim() : form.mailingAddress.trim(),
            emergencyContactName: form.emergencyContactName.trim(),
            emergencyContact: phoneDigits(form.emergencyContact)
        }
        setSaving(true)
        dispatch(saveMemberDetails(details, (err, saved) => {
            setSaving(false)
            if (err) {
                if (err.fields) {
                    const mapped = {}
                    Object.keys(err.fields).forEach((k) => { mapped[k] = FIELD_MESSAGES[err.fields[k]] || FIELD_MESSAGES.invalid })
                    setErrors(mapped)
                }
                setSaveError(err.message || "We couldn't save your details, please try again.")
                return
            }
            onConfirmed({ details: saved || details, placeOfBirth: form.placeOfBirth.trim() })
        }))
    }

    if (loading) {
        return <Grid container justifyContent="center" style={{ padding: 24 }}><CircularProgress size={24} /></Grid>
    }
    if (loadError) {
        return <div>
            <Typography style={{ color: '#a83232' }}>{loadError}</Typography>
            <Button color="primary" onClick={load} style={{ marginTop: 8 }}>Try again</Button>
        </div>
    }

    const field = (key, label, { helperText, ...props } = {}) => <TextField
        id={`details-${key}`}
        label={label}
        value={form[key]}
        onChange={set(key)}
        variant="outlined"
        size="small"
        fullWidth
        InputLabelProps={labelProps}
        {...props}
        error={!!errors[key]}
        helperText={errors[key] || helperText || ' '}
    />

    return <div data-testid="confirm-details">
        <Typography style={{ fontWeight: 600 }}>Confirm your details</Typography>
        <Typography variant="body2" color="textSecondary" style={{ margin: '4px 0 16px' }}>
            {intro || 'These go on your membership agreement and are saved to your account.'}
        </Typography>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>{field('firstName', 'First name', { required: true, autoComplete: 'given-name' })}</Grid>
            <Grid item xs={12} sm={6}>{field('lastName', 'Last name', { required: true, autoComplete: 'family-name' })}</Grid>
            <Grid item xs={12} sm={6}>{field('dateOfBirth', 'Date of birth', { required: true, type: 'date', inputProps: { max: new Date().toISOString().slice(0, 10) } })}</Grid>
            <Grid item xs={12} sm={6}>{field('placeOfBirth', 'Place of birth', { required: true, helperText: 'For the agreement only; not saved to your account' })}</Grid>
            <Grid item xs={12} sm={6}>{field('phoneNumber', 'Phone', { required: true, type: 'tel', autoComplete: 'tel', placeholder: '512-555-0147', inputProps: { maxLength: PHONE_MAX_LENGTH, inputMode: 'numeric' } })}</Grid>
            <Grid item xs={12}>{field('address', 'Home address', { required: true, autoComplete: 'street-address', multiline: true, minRows: 2 })}</Grid>
            <Grid item xs={12} style={{ paddingTop: 0 }}>
                <FormControlLabel
                    control={<Checkbox color="primary" checked={sameMailing} onChange={(e) => setSameMailing(e.target.checked)} />}
                    label="Mailing address is the same as home"
                />
            </Grid>
            {!sameMailing && <Grid item xs={12}>{field('mailingAddress', 'Mailing address', { multiline: true, minRows: 2 })}</Grid>}
            <Grid item xs={12} sm={6}>{field('emergencyContactName', 'Emergency contact name')}</Grid>
            <Grid item xs={12} sm={6}>{field('emergencyContact', 'Emergency contact phone', { type: 'tel', placeholder: '512-555-0147', inputProps: { maxLength: PHONE_MAX_LENGTH, inputMode: 'numeric' } })}</Grid>
        </Grid>
        {saveError && <Typography style={{ color: '#a83232', marginTop: 8 }}>{saveError}</Typography>}
        <Button color="primary" variant="contained" fullWidth size="large" style={{ marginTop: 16 }}
            disabled={saving} onClick={submit}
            startIcon={saving ? <CircularProgress size={14} /> : null}>
            {continueLabel}
        </Button>
    </div>
}

export default ConfirmDetails
